import { Component, Element, Event, Host, h, EventEmitter, Method, Prop, State, Listen } from '@stencil/core';
import logo from './logo';
import logoShort from "./logo-short";
import { Speed } from '../../entities';
import menuIcon from "./menu-icon";

@Component({
  tag: 'me-navbar',
  styleUrl: 'me-navbar.scss',
  shadow: false,
})
export class MeNavbar {

  @Element() host: HTMLMeNavbarElement;

  @Event() meNavbarMenuButtonClick: EventEmitter<void>;

  @Prop() backButton = false;

  @State() private innerWidth = innerWidth;

  private readonly logoBreakpoint = 375;

  /**
   * Modifier CSS Class Names
   *
   * We **must** apply these modifiers by using standard DOM API's.
   * Using `@State()` and the `render()` would break the slide-in animation.
   * That's because Stencil's virtual DOM implementation seems to optimize
   * against quick subsequent rerender attempts using debounce or something …
   */
  private readonly animatedModifier = 'me-navbar--animated';
  private readonly hiddenModifier = 'me-navbar--hidden';

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.innerWidth = window.innerWidth;
  }

  private get isVisible(): boolean {
    const rect = this.host.getBoundingClientRect();
    return rect.top >= -rect.height;
  }

  private get isHomepage(): boolean {
    return location.pathname === '/';
  }

  render() {
    const classList = [
      'me-navbar',
      this.backButton ? 'me-navbar--back-button' : null,
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList}>
        <nav class="me-navbar__actions me-navbar__actions--start">
          {this.backButton && !this.isHomepage
            ? (
              <me-button
                class="me-navbar__back-button"
                size="l"
                theme="primary"
                variant="link"
                onClick={this.navigateBack.bind(this)}
              >
                <i class="fa fa-chevron-left"/>
                <span class="me-navbar__back-button-label">Zurück</span>
              </me-button>
            )
            : (
              <slot name="start-actions" />
            )
          }
        </nav>
        <div
          class="me-navbar__logo"
          onClick={this.handleLogoClick.bind(this)}
          innerHTML={this.innerWidth >= this.logoBreakpoint
            ? logo
            : logoShort
          }
        ></div>
        <nav class="me-navbar__actions me-navbar__actions--end">
          <slot name="end-actions"></slot>
          <me-button
            class="me-navbar__menu-button"
            size="s"
            theme="primary"
            variant="link"
            aria-label="Menü"
            onClick={() => this.meNavbarMenuButtonClick.emit()}
            innerHTML={menuIcon}
          >
          </me-button>
        </nav>
        <nav class="me-navbar__body">
          <slot name="body"></slot>
        </nav>
      </Host>
    );
  }

  @Method()
  async shouldSlideIn(scrollSpeed: Speed, stickyHeight: number) {
    return !this.isVisible
      && scrollSpeed === Speed.Fast
      && stickyHeight < scrollY;
  }

  @Method()
  async slideOut(animated = true) {
    this.enableAnimations(animated);
    this.host.classList.add(this.hiddenModifier);
  }

  @Method()
  async slideIn(animated = true) {
    this.enableAnimations(animated);
    this.host.classList.remove(this.hiddenModifier);
  }

  private enableAnimations(animated = true) {
    if (animated) {
      this.host.classList.add(this.animatedModifier);
    } else {
      this.host.classList.remove(this.animatedModifier);
    }
  }

  private handleLogoClick() {
    if (scrollY > innerHeight) {
      scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      open('/', '_self');
    }
  }

  private navigateBack() {
    if (history.length > 1) {
      history.back();
    } else {
      location.replace('/');
    }
  }

}
